import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../../../utils/config';
import { useAppContext } from '../../../context';
import Footer from '../components/Footer';

const TYPE_MAP = {
  order:   { icon: '📦', bg: 'bg-blue-50',   text: 'text-blue-700'   },
  promo:   { icon: '🎉', bg: 'bg-orange-50', text: 'text-orange-600' },
  loyalty: { icon: '💳', bg: 'bg-amber-50',  text: 'text-amber-700'  },
  system:  { icon: '🔔', bg: 'bg-gray-50',   text: 'text-gray-600'   },
  support: { icon: '🎫', bg: 'bg-purple-50', text: 'text-purple-700' },
};

const FILTERS = ['all', 'unread', 'order', 'promo', 'loyalty', 'support', 'system'];

export default function CustomerNotificationsPage() {
  const { customerUser } = useAppContext();
  const [notifs, setNotifs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');

  useEffect(() => {
    if (!customerUser?.id) { setLoading(false); return; }
    axios
      .get(`${apiUrl}/notifications/customer/${customerUser.id}`)
      .then((res) => {
        if (res.data.status) setNotifs(res.data.notifications || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customerUser]);

  const markRead = (id) => {
    setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    axios.put(`${apiUrl}/notifications/${id}/read`).catch(() => {});
  };

  const dismiss = (id) => {
    setNotifs((prev) => prev.filter((n) => n.id !== id));
    axios.delete(`${apiUrl}/notifications/${id}`).catch(() => {});
  };

  const markAllRead = () => {
    setNotifs((prev) => prev.map((n) => ({ ...n, is_read: true })));
    if (customerUser?.id) {
      axios.put(`${apiUrl}/notifications/customer/${customerUser.id}/read-all`).catch(() => {});
    }
  };

  const filtered =
    filter === 'all'
      ? notifs
      : filter === 'unread'
      ? notifs.filter((n) => !n.is_read)
      : notifs.filter((n) => n.type === filter);

  const unreadCount = notifs.filter((n) => !n.is_read).length;

  if (!customerUser) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-5xl mb-4">🔔</p>
          <p className="font-bold text-gray-800 mb-1">Sign in to see your notifications</p>
          <p className="text-gray-400 text-sm mb-5">Stay updated on orders, promotions, and more.</p>
          <Link
            to="/login"
            className="bg-[#f97316] text-white px-6 py-3 rounded-2xl font-bold hover:bg-orange-600 transition"
          >
            Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="text-sm font-bold text-[#f97316] uppercase tracking-wider mb-1">
              Your Activity
            </p>
            <h1 className="font-serif text-3xl text-gray-900 flex items-center gap-3">
              Notifications
              {unreadCount > 0 && (
                <span className="bg-[#f97316] text-white text-sm font-black px-2.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h1>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-sm font-semibold text-[#f97316] hover:text-orange-600 transition mt-1"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap mb-6">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-2xl text-sm font-semibold capitalize transition ${
                filter === f
                  ? 'bg-[#f97316] text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f}
              {f === 'unread' && unreadCount > 0 && (
                <span className="ml-1.5 bg-white/30 text-white text-xs font-black px-1.5 py-0.5 rounded-full leading-none">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-5xl mb-4">🔕</p>
            <p className="font-bold text-gray-700 mb-1">No notifications</p>
            <p className="text-sm text-gray-400">
              {filter !== 'all' ? 'No notifications in this category.' : "You're all caught up!"}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((notif) => {
              const { icon, bg, text } = TYPE_MAP[notif.type] || TYPE_MAP.system;
              return (
                <div
                  key={notif.id}
                  onClick={() => markRead(notif.id)}
                  className={`flex gap-4 p-4 rounded-2xl border cursor-pointer transition-all hover:shadow-sm group ${
                    notif.is_read
                      ? 'bg-white border-gray-100'
                      : 'bg-orange-50/40 border-orange-100'
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 ${bg} ${text}`}
                  >
                    {icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm leading-snug ${
                          notif.is_read ? 'font-medium text-gray-600' : 'font-bold text-gray-900'
                        }`}
                      >
                        {notif.title || notif.message}
                      </p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!notif.is_read && (
                          <span className="w-2 h-2 bg-[#f97316] rounded-full" />
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); dismiss(notif.id); }}
                          className="text-gray-200 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 text-base leading-none"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                    {notif.title && notif.message && (
                      <p className="text-xs text-gray-400 mt-0.5">{notif.message}</p>
                    )}
                    <p className="text-[10px] text-gray-300 mt-1">
                      {notif.created_at
                        ? new Date(notif.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
